import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Toolbar, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Box, IconButton, CircularProgress, Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { menuItems, bottomMenuItems, appHighlightItems } from './MenuItems';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useKeycloak } from '@react-keycloak/web';
import { useUser } from '../../contexts/UserContext';
import { whiteLabelData } from './WhiteLabelData';
import { useThemeContext } from './ThemeContext';
// Lazy load the No-Code AI App

export default function DrawerContent({ isCollapsed, onToggleCollapse }) {
  const { keycloak, initialized } = useKeycloak();
  const { hasAccess, isSuperAdmin, userPermissions } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [clientData, setClientData] = useState(null);
  const { mode, toggleTheme } = useThemeContext();
  const theme = useTheme();

  useEffect(() => {
    // Function to get the subdomain from the hostname
    const getSubdomain = () => {
      const hostname = window.location.hostname;
      // If localhost, use a default value for testing
      //if (hostname === 'localhost') return 'baionity'; // Default for local testing
      
      const parts = hostname.split('.');
      if (parts.length >= 3) {
        return parts[0]; // Return the subdomain part
      }
      return null; // No subdomain found
    };

    // Find client data based on subdomain
    const subdomain = getSubdomain();
    const matchingClient = whiteLabelData.find(client => 
      client.domain.startsWith(subdomain) || client.client_id === subdomain
    );
    
    if (matchingClient) {
      setClientData(matchingClient);
    } else {
      setClientData({
        name: 'UT',
        logo_url: null,
      });
    }
  }, []);

  const filterMenuItems = (items) => {
    return items.filter(item => {
      // Always show section headers and logout
      if (item.type === 'section' || item.action === 'logout') {
        return true;
      }

      // Filter based on menu item
      switch (item.label) {
        case 'Cloud Management':
          return hasAccess('cloudManagement');
        case 'Admin':
          // Hide admin tab for client members (but show for super admins and other admin types)
          if (userPermissions?.clientRole === 'member' && !isSuperAdmin) {
            return false;
          }
          return hasAccess('admin');
        default:
          return true;
      }
    });
  };

  const handleItemClick = (item) => {
    if (item.action === 'logout') {
      keycloak.logout();
      navigate('/');
    } else if (item.action && typeof item.action === 'function') {
      // Execute the action function directly
      item.action();
    } else if (item.path) {
      // Check if path is an external URL (starts with http:// or https://)
      if (item.path.startsWith('http://') || item.path.startsWith('https://')) {
        window.open(item.path, '_blank'); // Open external links in a new tab
      } else {
        navigate(item.path); // Navigate to internal routes using router
      }
    }
  };

  const renderMenuItems = (items) => (
    items.map((item) => {
      if (item.type === 'section') {
        return (
          <Box key={item.label} sx={{ px: { xs: 1.5, sm: 2 }, py: 1 }}>
            {!isCollapsed && (
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                }}
              >
                {item.label}
              </Typography>
            )}
          </Box>
        );
      }

      return (
        <ListItem key={item.label} disablePadding sx={{ mb: { xs: 0.5, sm: 1 } }}>
          <ListItemButton
            onClick={() => handleItemClick(item)}
            sx={{
              borderRadius: 1.5,
              backgroundColor: location.pathname === item.path 
                ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.12)
                : 'transparent',
              minHeight: { xs: 40, sm: 44 },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              mb: 0.5,
              '&:hover': {
                backgroundColor: location.pathname === item.path 
                  ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.18)
                  : theme.palette.action.hover,
              }
            }}
          >
            <ListItemIcon sx={{ 
              color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
              minWidth: { xs: 36, sm: 40 },
              mr: { xs: 1, sm: 1.5 }
            }}>
              <item.icon sx={{ fontSize: { xs: '1.25rem', sm: '1.375rem' } }} />
            </ListItemIcon>
            {!isCollapsed && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                flex: 1,
                position: 'relative',
                minWidth: 0
              }}>
                <Typography
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    fontWeight: location.pathname === item.path ? 600 : 500,
                    color: location.pathname === item.path ? 'text.primary' : 'text.secondary',
                    flex: 1,
                    letterSpacing: '-0.01em',
                    ...(item.label === 'Agent Studio' && {
                      color: 'primary.main',
                    })
                  }}
                >
                  {item.label}
                </Typography>
                {item.comingSoon && (
                  <Chip 
                    label="Soon" 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.6rem',
                      height: '16px',
                      minHeight: '16px',
                      position: 'absolute',
                      right: 0,
                      '& .MuiChip-label': {
                        px: 0.5,
                        py: 0,
                        fontSize: '0.6rem',
                        lineHeight: 1
                      },
                      '& .MuiChip-root': {
                        borderWidth: '1px'
                      },
                      opacity: 0.8
                    }}
                  />
                )}
                {item.preview && (
                  <Chip 
                    label="Coming Soon" 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.6rem',
                      height: '16px',
                      minHeight: '16px',
                      position: 'absolute',
                      right: item.comingSoon ? 40 : 0,
                      '& .MuiChip-label': {
                        px: 0.5,
                        py: 0,
                        fontSize: '0.6rem',
                        lineHeight: 1
                      },
                      '& .MuiChip-root': {
                        borderWidth: '1px'
                      },
                      opacity: 0.8
                    }}
                  />
                )}
              </Box>
            )}
          </ListItemButton>
        </ListItem>
      );
    })
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      overflowX: 'hidden'
    }}>
      <Toolbar sx={{ 
        justifyContent: 'center',
        minHeight: { xs: 64, sm: 72 },
        px: { xs: 2, sm: 3 },
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        position: 'relative'
      }}>
        {!isCollapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <img 
              key={mode}
              src={clientData?.logo_url || "/WhiteLabelLogos/utw.png"} 
              alt={`${clientData?.name || "UT"} Logo`} 
              style={{ 
                height: '56px',
                maxWidth: '200px',
                objectFit: 'contain',
                filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'none'
              }} 
            />
          </Box>
        )}
        {!isCollapsed && (
          <IconButton 
            onClick={onToggleCollapse} 
            size="small"
            sx={{ 
              position: 'absolute',
              right: { xs: 8, sm: 12 },
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
        {isCollapsed && (
        <IconButton 
          onClick={onToggleCollapse} 
            size="small"
          sx={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
            }
          }}
        >
            <MenuIcon />
        </IconButton>
        )}
      </Toolbar>
      <List sx={{ 
        flexGrow: 1, 
        overflow: 'auto', 
        overflowX: 'hidden',
        px: { xs: 1, sm: 1.5 },
        py: { xs: 1.5, sm: 2 }
      }}>
        {renderMenuItems(filterMenuItems(menuItems))}
        <Box sx={{ 
          position: 'relative', 
          pt: { xs: 2, sm: 3 },
          my: { xs: 1, sm: 1.5 }
        }}>
        </Box>
        {renderMenuItems(filterMenuItems(appHighlightItems.filter(item => item.type !== 'section')))}
      </List>
      <Divider />
      <List sx={{ 
        overflowX: 'hidden',
        px: { xs: 1, sm: 1.5 },
        py: { xs: 1, sm: 1.5 }
      }}>
        {renderMenuItems(filterMenuItems(bottomMenuItems.filter(item => item.label !== 'Logout' && item.label !== 'Settings')))}
      </List>
    </Box>
  );
}
